import { useMemo, useState, type MouseEvent } from 'react';
import { api } from '../../lib/api';
import { addDays, dayRange, durationMinutes, fromLocalInput, localDate, minutesOfDay, startOfWeek, toLocalInput, today, zonedToUtc } from '../../lib/dates';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { setQuery, useLocation } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, Checkbox, cx, DataTable, Empty, ErrorBox, Field, Input, Loading, Modal, PageHeader, PersonPicker, Select, Tabs, Textarea, useToast } from '../../ui/components';
import { RRuleInput, useOptions } from '../../ui/schema';
import { AttendanceList } from '../trainer/trainer';

const PX = 1.1;

function useTrainers(slug: string) {
  return useApi(() => api<any[]>(`${T(slug)}/admin/trainers`), [slug]);
}

function useLessonTypes(slug: string) {
  return useApi(async () => (await api<{ items: any[] }>(`${T(slug)}/admin/r/lesson_types`, { query: { pageSize: 200, is_active: 'true' } })).items, [slug]);
}

function layout(items: any[]): Map<string, { lane: number; lanes: number }> {
  const out = new Map<string, { lane: number; lanes: number }>();
  const sorted = [...items].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  let group: any[] = [];
  let groupEnd = '';
  const flush = (): void => {
    const laneEnds: string[] = [];
    for (const s of group) {
      let lane = laneEnds.findIndex((e) => e <= s.starts_at);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(s.ends_at); } else laneEnds[lane] = s.ends_at;
      out.set(s.id, { lane, lanes: 0 });
    }
    for (const s of group) out.get(s.id)!.lanes = laneEnds.length;
    group = [];
  };
  for (const s of sorted) {
    if (group.length && s.starts_at >= groupEnd) flush();
    group.push(s);
    groupEnd = !groupEnd || s.ends_at > groupEnd || group.length === 1 ? s.ends_at : groupEnd;
  }
  flush();
  return out;
}

export function AdminPlannerPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const tz = gym.gym.timezone;
  const ws = gym.gym.policies.weekStartsOn ?? 1;
  const start = query.get('week') ?? startOfWeek(today(tz), ws);
  const loc = query.get('location') ?? '';
  const trainer = query.get('trainer') ?? '';
  const room = query.get('room') ?? '';
  const cal = useApi(() => api<any[]>(`${T(gym.slug)}/admin/calendar`, { query: { ...dayRange(start, addDays(start, 7), tz), location: loc, trainer, room } }), [start, loc, trainer, room]);
  const trainers = useTrainers(gym.slug);
  const rooms = useOptions(gym.slug, 'rooms');
  const [newAt, setNewAt] = useState<{ date: string; time: string } | null>(null);
  const [panel, setPanel] = useState<string | null>(null);
  const items = cal.data ?? [];
  const [minHour, maxHour] = useMemo(() => {
    let lo = 7;
    let hi = 21;
    for (const s of items) { lo = Math.min(lo, Math.floor(minutesOfDay(s.starts_at, tz) / 60)); hi = Math.max(hi, Math.ceil((minutesOfDay(s.starts_at, tz) + durationMinutes(s.starts_at, s.ends_at)) / 60)); }
    return [Math.max(0, lo), Math.min(24, hi)];
  }, [items]);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const height = (maxHour - minHour) * 60 * PX;
  const noTrainer = items.filter((s) => !s.trainer_person_id && s.status === 'scheduled').length;
  const clickEmpty = (e: MouseEvent<HTMLDivElement>, date: string): void => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const minutes = Math.floor(((e.clientY - rect.top) / PX + minHour * 60) / 15) * 15;
    setNewAt({ date, time: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}` });
  };
  return (
    <div className="page wide">
      <PageHeader title={i18n.t('nav.planner')} subtitle={noTrainer ? <span className="warn">{i18n.t('adminplanner.without_trainer', { count: noTrainer })}</span> : undefined}
        actions={<Button onClick={() => setNewAt({ date: today(tz) < start ? start : today(tz), time: '18:00' })}>+ {i18n.t('adminplanner.new_lesson')}</Button>} />
      <div className="toolbar">
        <div className="row gap-sm">
          <Button variant="secondary" size="sm" onClick={() => setQuery({ week: addDays(start, -7) })}>‹</Button>
          <Button variant="ghost" size="sm" onClick={() => setQuery({ week: null })}>{i18n.t('planner.today')}</Button>
          <Button variant="secondary" size="sm" onClick={() => setQuery({ week: addDays(start, 7) })}>›</Button>
          <h2 className="toolbar-title">{i18n.day(start, { day: 'numeric', month: 'short' })} – {i18n.day(addDays(start, 6), { day: 'numeric', month: 'short', year: 'numeric' })}</h2>
        </div>
        <div className="row gap-sm wrap">
          <Select value={loc} onChange={(e) => setQuery({ location: e.target.value })} placeholder={i18n.t('planner.all_locations')} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />
          <Select value={room} onChange={(e) => setQuery({ room: e.target.value })} placeholder={i18n.t('adminplanner.all_rooms')} options={rooms.map((r) => ({ value: r.id, label: r.label }))} />
          <Select value={trainer} onChange={(e) => setQuery({ trainer: e.target.value })} placeholder={i18n.t('adminplanner.all_trainers')} options={(trainers.data ?? []).map((t) => ({ value: t.person_id, label: `${t.first_name} ${t.last_name}` }))} />
        </div>
      </div>
      {cal.error ? <ErrorBox error={cal.error} onRetry={cal.reload} /> : !cal.data ? <Loading /> : (
        <div className="calendar-scroll">
          <div className="calendar">
            <div className="cal-hours" style={{ height }}>
              {Array.from({ length: maxHour - minHour }, (_, i) => <div key={i} style={{ height: 60 * PX }}>{String(minHour + i).padStart(2, '0')}:00</div>)}
            </div>
            {days.map((d) => {
              const daySessions = items.filter((s) => localDate(s.starts_at, tz) === d);
              const lanes = layout(daySessions);
              return (
                <div key={d} className="cal-day">
                  <div className={cx('cal-day-head', d === today(tz) && 'is-today')}>{i18n.day(d)}</div>
                  <div className="cal-day-body" style={{ height, backgroundSize: `100% ${60 * PX}px` }} onClick={(e) => clickEmpty(e, d)} title={i18n.t('adminplanner.click_to_add')}>
                    {daySessions.map((s) => {
                      const top = (minutesOfDay(s.starts_at, tz) - minHour * 60) * PX;
                      const h = Math.max(22, durationMinutes(s.starts_at, s.ends_at) * PX);
                      const l = lanes.get(s.id)!;
                      return (
                        <button type="button" key={s.id} onClick={() => setPanel(s.id)}
                          className={cx('cal-event', s.status === 'cancelled' && 'cancelled', !s.trainer_person_id && s.status === 'scheduled' && 'no-trainer')}
                          style={{ top, height: h, left: `${(100 / l.lanes) * l.lane}%`, width: `${100 / l.lanes}%`, background: s.lesson_color ?? undefined }}>
                          <strong>{i18n.time(s.starts_at)} {s.lesson_name}</strong>
                          <span>{s.trainer_name ?? i18n.t('adminplanner.no_trainer')}</span>
                          <span>{s.booked_count}/{s.capacity}{s.waitlist_count ? ` +${s.waitlist_count}` : ''}{s.room_name ? ` · ${s.room_name}` : ''}{s.outside_opening_hours ? ' ⚠' : ''}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {newAt && <NewLessonModal at={newAt} onClose={() => setNewAt(null)} onCreated={() => { setNewAt(null); cal.reload(); }} trainers={trainers.data ?? []} />}
      {panel && <SessionPanel id={panel} trainers={trainers.data ?? []} onClose={() => setPanel(null)} onChanged={cal.reload} />}
    </div>
  );
}

function NewLessonModal({ at, onClose, onCreated, trainers }: { at: { date: string; time: string }; onClose(): void; onCreated(): void; trainers: any[] }) {
  const gym = useGym();
  const i18n = useI18n();
  const types = useLessonTypes(gym.slug);
  const rooms = useApi(async () => (await api<{ items: any[] }>(`${T(gym.slug)}/admin/r/rooms`, { query: { pageSize: 200 } })).items, []);
  const [v, setV] = useState({ lesson_type_id: '', location_id: gym.locations[0]?.id ?? '', room_id: '', trainer_person_id: '', date: at.date, time: at.time, duration: '', capacity: '', end_date: '', notes: '' });
  const [rrule, setRrule] = useState<any>(null);
  const lt = types.data?.find((x) => x.id === v.lesson_type_id);
  const duration = Number(v.duration || lt?.duration_min || 60);
  const capacity = Number(v.capacity || lt?.capacity || 1);
  const tz = gym.gym.timezone;
  const create = async (): Promise<void> => {
    const common = { lesson_type_id: v.lesson_type_id, location_id: v.location_id, room_id: v.room_id || null, trainer_person_id: v.trainer_person_id || null, capacity };
    if (rrule?.freq) {
      await api(`${T(gym.slug)}/admin/r/lesson_series`, { body: { ...common, start_date: v.date, end_date: v.end_date || null, start_time: v.time, duration_min: duration, rrule } });
    } else {
      const startsAt = zonedToUtc(v.date, v.time, tz);
      await api(`${T(gym.slug)}/admin/r/sessions`, { body: { ...common, starts_at: startsAt.toISOString(), ends_at: new Date(startsAt.getTime() + duration * 60_000).toISOString(), notes: v.notes || null } });
    }
    onCreated();
  };
  return (
    <Modal wide title={i18n.t('adminplanner.new_lesson')} onClose={onClose} footer={<AsyncButton disabled={!v.lesson_type_id || !v.location_id} onClick={create}>{i18n.t('common.create')}</AsyncButton>}>
      <div className="grid-3">
        <Field label={`${i18n.field('lesson_type_id')} *`}>
          <Select value={v.lesson_type_id} onChange={(e) => setV({ ...v, lesson_type_id: e.target.value })} placeholder="—" options={(types.data ?? []).map((t) => ({ value: t.id, label: t.name }))} />
        </Field>
        <Field label={`${i18n.field('location_id')} *`}><Select value={v.location_id} onChange={(e) => setV({ ...v, location_id: e.target.value, room_id: '' })} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} /></Field>
        <Field label={i18n.field('room_id')}><Select value={v.room_id} onChange={(e) => setV({ ...v, room_id: e.target.value })} placeholder="—" options={(rooms.data ?? []).filter((r) => r.location_id === v.location_id).map((r) => ({ value: r.id, label: `${r.name} (${r.capacity ?? '∞'})` }))} /></Field>
        <Field label={i18n.field('date')}><Input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} /></Field>
        <Field label={i18n.field('start_time')}><Input type="time" step={300} value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })} /></Field>
        <Field label={i18n.field('duration_min')}><Input type="number" min={5} placeholder={String(lt?.duration_min ?? 60)} value={v.duration} onChange={(e) => setV({ ...v, duration: e.target.value })} /></Field>
        <Field label={i18n.field('capacity')}><Input type="number" min={1} placeholder={String(lt?.capacity ?? '')} value={v.capacity} onChange={(e) => setV({ ...v, capacity: e.target.value })} /></Field>
        <Field label={i18n.t('planner.trainer')} hint={i18n.t('adminplanner.trainer_hint')}>
          <Select value={v.trainer_person_id} onChange={(e) => setV({ ...v, trainer_person_id: e.target.value })} placeholder={i18n.t('adminplanner.open_for_claims')} options={trainers.map((t) => ({ value: t.person_id, label: `${t.first_name} ${t.last_name}` }))} />
        </Field>
      </div>
      <Field label={i18n.t('availability.repeat')}><RRuleInput value={rrule} onChange={setRrule} /></Field>
      {rrule?.freq ? (
        <Field label={i18n.field('end_date')} hint={i18n.t('adminplanner.series_end_hint')}><Input type="date" value={v.end_date} onChange={(e) => setV({ ...v, end_date: e.target.value })} /></Field>
      ) : (
        <Field label={i18n.field('notes')}><Textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></Field>
      )}
      {lt && <p className="small muted">{i18n.t('adminplanner.token_rule', { required: lt.tokens_required ?? 1 })}</p>}
    </Modal>
  );
}

function SessionPanel({ id, onClose, onChanged, trainers }: { id: string; onClose(): void; onChanged(): void; trainers: any[] }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const s = useApi(() => api(`${T(gym.slug)}/admin/r/sessions/${id}`), [id]);
  const [tab, setTab] = useState('details');
  const [edit, setEdit] = useState<Record<string, any>>({});
  const [scope, setScope] = useState('this');
  const [reason, setReason] = useState('');
  const [person, setPerson] = useState<{ id: string; label: string } | null>(null);
  const [override, setOverride] = useState(false);
  const fit = useApi(tab === 'trainer' ? () => api<any[]>(`${T(gym.slug)}/admin/sessions/${id}/trainers`) : null, [tab, id]);
  const rooms = useOptions(gym.slug, 'rooms');
  const types = useOptions(gym.slug, 'lesson_types');
  if (s.error) return <Modal title="—" onClose={onClose}><ErrorBox error={s.error} /></Modal>;
  if (!s.data) return <Modal title="…" onClose={onClose}><Loading /></Modal>;
  const se = s.data;
  const tz = gym.gym.timezone;
  const lessonName = types.find((t) => t.id === se.lesson_type_id)?.label ?? '';
  const refresh = (): void => { s.reload(); onChanged(); };
  const future = se.starts_at > new Date().toISOString() && se.status === 'scheduled';
  const assign = async (personId: string | null): Promise<void> => {
    await api(`${T(gym.slug)}/admin/sessions/${id}/edit`, { body: { patch: { trainer_person_id: personId }, scope } });
    toast.ok(i18n.t('common.saved'));
    refresh();
    fit.reload();
  };
  return (
    <Modal wide title={<>{lessonName} · {i18n.date(se.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })} {i18n.time(se.starts_at)} <Badge value={se.status} /></>} onClose={onClose}>
      <Tabs value={tab} onChange={setTab} tabs={[
        { id: 'details', label: i18n.t('adminplanner.tab_details') },
        { id: 'attendees', label: `${i18n.t('planner.attendees')} (${se.booked_count}/${se.capacity}${se.waitlist_count ? ` +${se.waitlist_count}` : ''})` },
        { id: 'trainer', label: i18n.t('planner.trainer'), hidden: !future },
      ]} />
      {tab === 'details' && (
        <div className="stack">
          {se.series_id && future && (
            <Field label={i18n.t('adminplanner.apply_to')}>
              <Select value={scope} onChange={(e) => setScope(e.target.value)} options={['this', 'following', 'all'].map((x) => ({ value: x, label: i18n.t(`adminplanner.scope_${x}`) }))} />
            </Field>
          )}
          <div className="grid-3">
            <Field label={i18n.field('starts_at')}>
              <Input type="datetime-local" disabled={!future} value={toLocalInput(edit.starts_at ?? se.starts_at, tz)} onChange={(e) => {
                const startsAt = fromLocalInput(e.target.value, tz)!;
                setEdit({ ...edit, starts_at: startsAt, ends_at: new Date(Date.parse(startsAt) + durationMinutes(se.starts_at, se.ends_at) * 60_000).toISOString() });
              }} />
            </Field>
            <Field label={i18n.field('duration_min')}>
              <Input type="number" min={5} disabled={!future} value={durationMinutes(edit.starts_at ?? se.starts_at, edit.ends_at ?? se.ends_at)} onChange={(e) => {
                const startsAt = edit.starts_at ?? se.starts_at;
                setEdit({ ...edit, starts_at: startsAt, ends_at: new Date(Date.parse(startsAt) + Number(e.target.value) * 60_000).toISOString() });
              }} />
            </Field>
            <Field label={i18n.field('capacity')}><Input type="number" min={Math.max(1, se.booked_count)} disabled={!future} value={edit.capacity ?? se.capacity} onChange={(e) => setEdit({ ...edit, capacity: Number(e.target.value) })} /></Field>
            <Field label={i18n.field('room_id')}><Select disabled={!future} value={edit.room_id ?? se.room_id ?? ''} placeholder="—" onChange={(e) => setEdit({ ...edit, room_id: e.target.value || null })} options={rooms.map((r) => ({ value: r.id, label: r.label }))} /></Field>
            <Field label={i18n.field('visibility')}><Select disabled={!future} value={edit.visibility ?? se.visibility ?? 'public'} onChange={(e) => setEdit({ ...edit, visibility: e.target.value })} options={['public', 'members', 'hidden'].map((x) => ({ value: x, label: i18n.option(x) }))} /></Field>
            <Field label={i18n.field('trial_spots')}><Input type="number" min={0} disabled={!future} value={edit.trial_spots ?? se.trial_spots ?? ''} onChange={(e) => setEdit({ ...edit, trial_spots: e.target.value === '' ? null : Number(e.target.value) })} /></Field>
          </div>
          <Field label={i18n.field('notes')}><Textarea rows={2} value={edit.notes ?? se.notes ?? ''} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Field>
          {se.trainer_fee_cents !== null && se.trainer_fee_cents !== undefined && <p className="small muted">{i18n.t('trainer.fee')}: {i18n.money(se.trainer_fee_cents)}</p>}
          {se.cancel_reason && <p className="warn">{se.cancel_reason}</p>}
          <div className="row between wrap gap-sm">
            <AsyncButton disabled={!Object.keys(edit).length} onClick={async () => {
              await api(`${T(gym.slug)}/admin/sessions/${id}/edit`, { body: { patch: edit, scope } });
              setEdit({});
              toast.ok(i18n.t('adminplanner.saved_notified'));
              refresh();
            }}>{i18n.t('common.save')}</AsyncButton>
            {future && (
              <div className="row gap-sm">
                <Input placeholder={i18n.t('adminplanner.cancel_reason')} value={reason} onChange={(e) => setReason(e.target.value)} />
                <AsyncButton variant="danger" disabled={!reason.trim()} confirm={i18n.t('adminplanner.cancel_confirm', { count: se.booked_count })} onClick={async () => {
                  const r = await api(`${T(gym.slug)}/admin/sessions/${id}/cancel`, { body: { reason } });
                  toast.ok(i18n.t('adminplanner.cancelled', { count: r.affected ?? se.booked_count }));
                  refresh();
                }}>{i18n.t('adminplanner.cancel_lesson')}</AsyncButton>
              </div>
            )}
          </div>
        </div>
      )}
      {tab === 'attendees' && (
        <div className="stack">
          <AttendanceList sessionId={id} canRemove onChanged={refresh} />
          {future && (
            <Card title={i18n.t('adminplanner.add_person')}>
              <PersonPicker slug={gym.slug} value={person} onChange={setPerson} />
              <Checkbox checked={override} onChange={setOverride} label={i18n.t('adminplanner.override')} hint={i18n.t('adminplanner.override_hint')} />
              <div className="row gap-sm">
                <AsyncButton disabled={!person} onClick={async () => {
                  const r = await api(`${T(gym.slug)}/admin/sessions/${id}/bookings`, { body: { personId: person!.id, override, joinWaitlist: se.booked_count >= se.capacity } });
                  toast.ok(i18n.t(r.status === 'waitlisted' ? 'planner.joined_waitlist' : r.status === 'pending_payment' ? 'adminplanner.payment_link_created' : 'planner.booked'));
                  setPerson(null);
                  refresh();
                }}>{i18n.t(se.booked_count >= se.capacity ? 'planner.join_waitlist' : 'planner.book')}</AsyncButton>
              </div>
            </Card>
          )}
        </div>
      )}
      {tab === 'trainer' && (
        <div className="stack">
          {se.series_id && <Field label={i18n.t('adminplanner.apply_to')}><Select value={scope} onChange={(e) => setScope(e.target.value)} options={['this', 'following', 'all'].map((x) => ({ value: x, label: i18n.t(`adminplanner.scope_${x}`) }))} /></Field>}
          <div className="row gap-sm wrap">
            <AsyncButton variant="secondary" onClick={async () => { const r = await api(`${T(gym.slug)}/admin/sessions/${id}/notify-trainers`, { body: {} }); toast.ok(i18n.t('adminplanner.notified', { count: r.notified })); }}>{i18n.t('adminplanner.notify_trainers')}</AsyncButton>
            {se.trainer_person_id && <AsyncButton variant="ghost" onClick={() => assign(null)}>{i18n.t('adminplanner.remove_trainer')}</AsyncButton>}
          </div>
          {!fit.data ? <Loading /> : fit.data.length === 0 ? <Empty>{i18n.t('adminplanner.no_trainers')} ({trainers.length})</Empty> : (
            <DataTable rows={fit.data} rowKey="personId" columns={[
              { key: 'name', label: i18n.field('name'), render: (t) => <>{t.name} {t.current && <Badge value="active" label={i18n.t('adminplanner.current')} />}</> },
              { key: 'feeCents', label: i18n.t('trainer.fee'), render: (t) => i18n.money(t.feeCents) },
              { key: 'reasons', label: i18n.t('adminplanner.fit'), render: (t) => (t.reasons.length ? t.reasons.map((r: string) => <span key={r} className={cx('badge', t.canAssign ? 'badge-warn' : 'badge-bad')}>{i18n.t(`fit.${r}`)}</span>) : <Badge value="available" label={i18n.t('fit.ok')} />) },
              { key: 'x', label: '', render: (t) => (!t.current && t.canAssign ? <AsyncButton size="sm" onClick={() => assign(t.personId)}>{i18n.t('adminplanner.assign')}</AsyncButton> : null) },
            ]} />
          )}
        </div>
      )}
    </Modal>
  );
}

export function OpenLessonsAdminPage() {
  const gym = useGym();
  const i18n = useI18n();
  const open = useApi(() => api<any[]>(`${T(gym.slug)}/admin/open-sessions`), []);
  const claims = useApi(() => api<any[]>(`${T(gym.slug)}/admin/claims`), []);
  const reload = (): void => { open.reload(); claims.reload(); };
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.open_lessons')} subtitle={i18n.t('adminplanner.open_help')} />
      <Card title={i18n.t('adminplanner.claims')}>
        {!claims.data ? <Loading /> : <DataTable rows={claims.data} columns={[
          { key: 'starts_at', label: i18n.t('planner.when'), render: (c) => `${i18n.date(c.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })} ${i18n.time(c.starts_at)}` },
          { key: 'lesson_name', label: i18n.t('planner.lesson') },
          { key: 'trainer', label: i18n.t('planner.trainer'), render: (c) => `${c.first_name} ${c.last_name}` },
          { key: 'x', label: '', render: (c) => (
            <div className="row gap-sm end">
              <AsyncButton size="sm" onClick={async () => { await api(`${T(gym.slug)}/admin/claims/${c.id}/approve`, { body: {} }); reload(); }}>{i18n.t('adminplanner.approve')}</AsyncButton>
              <AsyncButton size="sm" variant="ghost" onClick={async () => { await api(`${T(gym.slug)}/admin/claims/${c.id}/reject`, { body: {} }); reload(); }}>{i18n.t('adminplanner.reject')}</AsyncButton>
            </div>
          ) },
        ]} empty={i18n.t('adminplanner.no_claims')} />}
      </Card>
      <Card title={i18n.t('adminplanner.without_trainer_title')}>
        {!open.data ? <Loading /> : <DataTable rows={open.data} columns={[
          { key: 'startsAt', label: i18n.t('planner.when'), render: (s) => <>{i18n.date(s.startsAt, { weekday: 'short', day: 'numeric', month: 'short' })} {i18n.time(s.startsAt)} {s.urgent && <span className="badge badge-bad">{i18n.t('trainer.urgent')}</span>}</> },
          { key: 'lessonName', label: i18n.t('planner.lesson') },
          { key: 'locationName', label: i18n.t('planner.where') },
          { key: 'booked', label: i18n.t('planner.attendees'), render: (s) => `${s.booked}/${s.capacity}` },
          { key: 'x', label: '', render: (s) => <AsyncButton size="sm" variant="secondary" onClick={async () => { await api(`${T(gym.slug)}/admin/sessions/${s.id}/notify-trainers`, { body: {} }); }} success={i18n.t('adminplanner.notified_short')}>{i18n.t('adminplanner.notify_trainers')}</AsyncButton> },
        ]} empty={i18n.t('trainer.no_open')} />}
      </Card>
    </div>
  );
}
